import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import {
  authClient,
  getStoredToken,
  setStoredToken,
  clearStoredToken,
} from '@/lib/auth-client';

// Use empty string for same-origin requests (proxied via Vite dev server)
const API_BASE_URL = '';

export interface User {
  id: string;
  sub: string;
  email: string;
  name: string;
  emailVerified?: boolean;
  avatarUrl?: string;
  role?: string;
  organizationId?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  loginWithGoogle: () => void;
  logout: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  updateUser: (updates: Partial<User>) => void;
  getAuthHeaders: () => Record<string, string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Public routes that don't require authentication
const PUBLIC_ROUTES = [
  '/login',
  '/callback',
  '/forgot-password',
  '/reset-password',
  '/features',
  '/concepts',
  '/use-cases',
  '/pricing',
  '/about',
  '/contact',
  '/careers',
  '/legal',
  '/resources',
  '/info',
];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const isPublicRoute = PUBLIC_ROUTES.some(route => pathname?.startsWith(route)) || pathname === '/';

  // Get authorization headers for API calls
  const getAuthHeaders = useCallback((): Record<string, string> => {
    const token = getStoredToken();
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  }, []);

  // Check session using Bearer token (no cookies)
  const checkSession = useCallback(async (): Promise<boolean> => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/get-session`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        // Don't send cookies to avoid conflicts with bearer auth
        credentials: 'omit',
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.user) {
          setUser({
            id: data.user.id,
            sub: data.user.id,
            email: data.user.email || '',
            name: data.user.name || '',
            emailVerified: data.user.emailVerified,
            avatarUrl: data.user.image || undefined,
          });
          return true;
        }
      }

      // Token invalid, clear it
      clearStoredToken();
      setUser(null);
      return false;
    } catch (error) {
      console.error('Session check failed:', error);
      clearStoredToken();
      setUser(null);
      return false;
    }
  }, []);

  // Initial session check - only run once on mount
  useEffect(() => {
    let cancelled = false;

    const initSession = async () => {
      setIsLoading(true);
      const authenticated = await checkSession();

      if (cancelled) return;

      setIsLoading(false);

      // Redirect to login if not authenticated and not on public route
      if (!authenticated && !isPublicRoute) {
        navigate({ to: '/login', search: { redirect: pathname || '/overview' } });
      }
    };

    initSession();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Handle route changes for protected routes
  useEffect(() => {
    if (isLoading) return; // Don't redirect while loading

    if (!user && !isPublicRoute) {
      navigate({ to: '/login', search: { redirect: pathname || '/overview' } });
    }
  }, [user, isPublicRoute, isLoading, navigate, pathname]);

  // Periodic session validation (every 5 minutes)
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(async () => {
      const valid = await checkSession();
      if (!valid) {
        navigate({ to: '/login' });
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [user, navigate, checkSession]);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/sign-in/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        // Don't send cookies to avoid conflicts with bearer auth
        credentials: 'omit',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return { success: false, error: errorData.message || 'Login failed' };
      }

      // Better Auth bearer plugin returns token in 'set-auth-token' response header
      const bearerToken = response.headers.get('set-auth-token');
      if (bearerToken) {
        setStoredToken(bearerToken);
      }

      const data = await response.json();

      if (data.user) {
        setUser({
          id: data.user.id,
          sub: data.user.id,
          email: data.user.email || '',
          name: data.user.name || '',
          emailVerified: data.user.emailVerified,
          avatarUrl: data.user.image || undefined,
        });
        return { success: true };
      }

      return { success: false, error: 'Login failed. Please check your credentials.' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const loginWithGoogle = async () => {
    try {
      // Better Auth social login requires POST request
      const response = await fetch(`${API_BASE_URL}/api/auth/sign-in/social`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: 'google',
          callbackURL: `${window.location.origin}/callback`,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.url) {
          // Redirect to Google OAuth URL
          window.location.href = data.url;
        }
      } else {
        console.error('Google login request failed:', response.status);
      }
    } catch (error) {
      console.error('Google login error:', error);
    }
  };

  const logout = async () => {
    const token = getStoredToken();
    try {
      await fetch(`${API_BASE_URL}/api/auth/sign-out`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearStoredToken();
      setUser(null);
      navigate({ to: '/login' });
    }
  };

  const refreshSession = useCallback(async (): Promise<boolean> => {
    return checkSession();
  }, [checkSession]);

  const updateUser = (updates: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...updates });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        loginWithGoogle,
        logout,
        refreshSession,
        updateUser,
        getAuthHeaders,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthContext };
